import React from "react";
import useFitText from "use-fit-text";
import { MarketBasketItem, Price } from "./MarketModal";
import { ensLogo, rightArrow, udLogo, slLogo, gdLogo } from "./svgs";
import { convertPrice } from "./MarketConfirmPage";

type SuperlinkMarketSearchResultProps = {
  domain: string;
  nameService: string;
  price: Price;
  currency: string;
  region: string;
  pillText?: string;
  taken?: boolean;
  features: string[];
  currencyEstimates: [
    {
      symbol: string;
      name: string;
      exhangeRate: number;
    }
  ];
  addToBasket: (item: MarketBasketItem) => void;
};

const containerStyle: React.CSSProperties = {
  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
  cursor: "pointer",
  padding: "10px",
  paddingLeft: "24px",
  paddingRight: "24px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  backgroundColor: "#FFFFFF",
};

const containerStyleTaken: React.CSSProperties = {
  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
  cursor: "pointer",
  padding: "10px",
  paddingLeft: "24px",
  paddingRight: "24px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  backgroundColor: "rgba(248, 248, 248, 1)",
};

const containerInnerLeftStyle: React.CSSProperties = {
  padding: "10px",
  paddingLeft: "0px",
  paddingRight: "0px",
  display: "flex",
  flexShrink: 1,
  flexGrow: 1,
  overflow: "hidden",
  flexDirection: "column",
  justifyContent: "left",
  gap: 5,
};

const containerInnerLeftStyleTaken: React.CSSProperties = {
  padding: "10px",
  paddingLeft: "0px",
  paddingRight: "0px",
  display: "flex",
  flexShrink: 1,
  flexGrow: 1,
  overflow: "hidden",
  flexDirection: "column",
  justifyContent: "left",
  color: "rgba(0, 0, 0, 0.3)",
  gap: 5,
};

const containerInnerRightStyle: React.CSSProperties = {
  padding: "10px",
  paddingLeft: "0px",
  paddingRight: "0px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
};

const domainStyle: (fontSize: string) => React.CSSProperties = (fontSize) => {
  return {
    fontSize,
    fontFamily: "GT Planar Medium",
  };
};

const nameServiceStyle: React.CSSProperties = {
  color: "rgba(0, 0, 0, 0.7)",
  textDecoration: "none",
  fontSize: "12px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  alignContent: "center",
  gap: 5,
};

const priceStyle: React.CSSProperties = {
  color: "rgba(0, 0, 0, 0.9)",
  fontSize: "16px",
  display: "flex",
  justifyContent: "right",
};

const logoStyle: React.CSSProperties = {
  width: "16px",
  height: "16px",
  borderRadius: 10000,
  justifyContent: "center",
  alignItems: "center",
  alignContent: "center",
  flex: "0 0 auto",
  display: "flex",
};

export const formatCurrency = (
  price: number,
  currency: string,
  region: string
): string => {
  if (currency == "USD") {
    return new Intl.NumberFormat(`en-${region}`, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(price);
  }
  if (price > 0.001) return `${price.toFixed(4)} ${currency}`;
  return `${price.toFixed(8)} ${currency}`;
};

const SuperlinkMarketSearchResult: React.FC<
  SuperlinkMarketSearchResultProps
> = ({
  domain,
  nameService,
  price,
  addToBasket,
  currency,
  region,
  pillText,
  taken,
  currencyEstimates,
  features,
}) => {
  const { fontSize, ref } = useFitText();
  return (
    <div
      style={taken ? containerStyleTaken : containerStyle}
      onClick={() => {
        if (taken) return;
        addToBasket({
          domain: domain,
          nameService: nameService,
          price: price,
          years: 1,
          currency: currency,
          region: region,
          features: features,
        });
      }}
    >
      <div
        style={taken ? containerInnerLeftStyleTaken : containerInnerLeftStyle}
      >
        {pillText && (
          <div
            style={{
              paddingLeft: 8,
              paddingRight: 8,
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 1000,
              fontSize: "11px",
              color: "rgba(41, 155, 128, 1)",
              width: "fit-content",
              backgroundColor: "rgba(41, 155, 128, 0.1)",
            }}
          >
            {pillText.toUpperCase()}
          </div>
        )}
        <div ref={ref} style={domainStyle(fontSize)}>
          {domain}
        </div>
        {formatNameService(nameService)}
      </div>
      {taken && (
        <div style={containerInnerRightStyle}>
          <span
            style={{
              paddingLeft: 8,
              paddingRight: 8,
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 1000,
              fontSize: "11px",
              color: "rgba(207, 21, 88, 1)",
              width: "fit-content",
              backgroundColor: "rgba(207, 21, 88, 0.1)",
            }}
          >
            TAKEN
          </span>
        </div>
      )}
      {!taken && (
        <div style={containerInnerRightStyle}>
          {formatPriceTotal(
            nameService,
            convertPrice(currency, price, currencyEstimates),
            currency,
            "US", 1
          )}
          {rightArrow("black")}
        </div>
      )}
    </div>
  );
};

export const formatNameService = (key: string) => {
  switch (key.toLowerCase()) {
    case "superlink":
      return (
        <div style={nameServiceStyle}>
          <div style={logoStyle}>{slLogo}</div>
          {"Superlink"}
        </div>
      );
    case "ens":
      return (
        <div style={nameServiceStyle}>
          <div style={logoStyle}>{ensLogo}</div>
          {"ENS"}
        </div>
      );
    case "ud":
      return (
        <div style={nameServiceStyle}>
          <div style={logoStyle}>{udLogo}</div>
          {"Unstoppable"}
        </div>
      );
    case "icann":
      return (
        <div style={nameServiceStyle}>
          <div style={logoStyle}>{gdLogo}</div>
          {"GoDaddy"}
        </div>
      );
    default:
      return <div style={nameServiceStyle}>{key}</div>;
  }
};

export const formatPrice = (
  nameService: string,
  price: Price,
  currency: string,
  region: string
) => {
  switch (nameService.toLowerCase()) {
    case "superlink":
      return (
        <div
          style={{
            display: "flex",
            width: "-webkit-fill-available",
            justifyContent: "flex-end",
          }}
        >
          <div style={priceStyle}>
            <div>{formatCurrency(price.setup, currency, region)}</div>
          </div>
        </div>
      );
    case "ens":
      return (
        <div style={{ width: "-webkit-fill-available" }}>
          <div style={priceStyle}>
            <div>Rent:</div>
            <div>
              {formatCurrency(price.ens_rent, currency, region)}
              {" / yr"}
            </div>
          </div>
          <div style={priceStyle}>
            <div>Network Fee:</div>
            <div>{formatCurrency(price.ens_setup_gas, currency, region)}</div>
          </div>
        </div>
      );
    case "ud":
      return (
        <div
          style={{
            display: "flex",
            width: "-webkit-fill-available",
            justifyContent: "flex-end",
          }}
        >
          <div style={priceStyle}>
            <div>{formatCurrency(price.setup, currency, region)}</div>
          </div>
        </div>
      );
    case "icann":
      return (
        <div style={{ width: "-webkit-fill-available" }}>
          <div style={priceStyle}>
            <div>Registration:</div>
            <div>
              {formatCurrency(price.setup, currency, region)}
              {" / yr"}
            </div>
          </div>
          <div style={priceStyle}>
            <div>Renewal:</div>
            <div>
              {formatCurrency(price.renewal, currency, region)}
              {" / yr"}
            </div>
          </div>
          <div style={priceStyle}>
            <div>ENS Bridge:</div>
            <div>{formatCurrency(price.ens_bridge, currency, region)}</div>
          </div>
        </div>
      );
    default:
      return <div style={nameServiceStyle}>{0}</div>;
  }
};

export const formatPriceTotal = (
  nameService: string,
  price: Price,
  currency: string,
  region: string,
  years: number
) => {
  switch (nameService.toLowerCase()) {
    case "superlink":
      return (
        <div>
          <div style={priceStyle}>
            {formatCurrency(
              calculateTotalPrice("superlink", price, years),
              currency,
              region
            )}
          </div>
        </div>
      );
    case "ens":
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <div style={priceStyle}>
            {formatCurrency(
              price.ens_setup_gas,
              currency,
              region
            )}
          </div>
          <div
            style={{
              color: "rgba(0, 0, 0, 0.7)",
              fontSize: "12px",
            }}
          >
            plus {formatCurrency(price.ens_rent, currency, region)} / yr
          </div>
        </div>
      );
    case "ud":
      return (
        <div>
          <div style={priceStyle}>
            {formatCurrency(
              calculateTotalPrice("ud", price, years),
              currency,
              region
            )}
          </div>
        </div>
      );
    case "icann":
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <div style={priceStyle}>
            {formatCurrency(
              price.ens_bridge,
              currency,
              region
            )}
          </div>
          <div
            style={{
              color: "rgba(0, 0, 0, 0.7)",
              fontSize: "12px",
            }}
          >
            plus {formatCurrency(price.renewal, currency, region)} / yr
          </div>
        </div>
      );
    default:
      return <div style={nameServiceStyle}>{0}</div>;
  }
};

export const formatPriceTotalConfirm = (
  nameService: string,
  price: Price,
  currency: string,
  region: string,
  years: number
) => {
  switch (nameService.toLowerCase()) {
    case "superlink":
      return (
        <div>
          <div style={priceStyle}>
            {formatCurrency(
              calculateTotalPrice("superlink", price, years),
              currency,
              region
            )}
          </div>
        </div>
      );
    case "ens":
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <div style={priceStyle}>
            {formatCurrency(
              calculateTotalPrice("ens", price, years),
              currency,
              region
            )}
          </div>
        </div>
      );
    case "ud":
      return (
        <div>
          <div style={priceStyle}>
            {formatCurrency(
              calculateTotalPrice("ud", price, years),
              currency,
              region
            )}
          </div>
        </div>
      );
    case "icann":
      return (
        <div>
          <div style={priceStyle}>
            {formatCurrency(
              calculateTotalPrice("icann", price, years),
              currency,
              region
            )}
          </div>
        </div>
      );
    default:
      return <div style={nameServiceStyle}>{0}</div>;
  }
};

export const calculateTotalPrice = (
  nameService: string,
  price: Price,
  years: number
) => {
  switch (nameService.toLowerCase()) {
    case "superlink":
      return price.setup;
    case "ens":
      return price.ens_rent * years + price.ens_setup_gas;
    case "ud":
      return price.setup;
    case "icann":
      if (years > 0) {
        return price.setup + (price.renewal * (years - 1) + price.ens_bridge);
      } else {
        return 0;
      }
    default:
      return price.setup;
  }
};

export default SuperlinkMarketSearchResult;
